import { supabase } from '../supabaseClient';
export const OFFICIAL_CHECKOUT_HANDOFF_VERSION = 2;

const MAX_ITEMS = 20;

const SOURCE_TYPES = {
  shop_products: 'shop_product',
  boutique_forfaits: 'boutique_forfait',
  presale_offers: 'presale_offer',
} as const;

type OfficialSourceType =
  | 'shop_product'
  | 'boutique_forfait'
  | 'presale_offer';

type ExploreCartItem = {
  id?: unknown;
  source_id?: unknown;
  sourceId?: unknown;
  source_table?: unknown;
  sourceTable?: unknown;
  source_type?: unknown;
  sourceType?: unknown;
  quantity?: unknown;
};

type HandoffItem = {
  source_type: OfficialSourceType;
  source_id: string;
  quantity: number;
};


const UUID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function text(
  value: unknown,
  maximum: number,
): string {
  return typeof value === 'string'
    ? value.trim().slice(0, maximum)
    : '';
}

function normalizeItem(
  item: ExploreCartItem,
): HandoffItem {
  const rawSource = text(
    item.source_table ||
      item.sourceTable ||
      item.source_type ||
      item.sourceType,
    50,
  );

  const sourceType =
    SOURCE_TYPES[
      rawSource as keyof typeof SOURCE_TYPES
    ] || rawSource;

  if (
    sourceType !== 'shop_product' &&
    sourceType !== 'boutique_forfait' &&
    sourceType !== 'presale_offer'
  ) {
    throw new Error(
      'Un article ne provient pas d’un catalogue officiel.',
    );
  }

  const sourceId = text(
    item.source_id ||
      item.sourceId ||
      item.id,
    100,
  );

  if (!UUID.test(sourceId)) {
    throw new Error(
      'Un article possède un identifiant officiel invalide.',
    );
  }

  const quantity = Number(item.quantity);

  if (
    !Number.isInteger(quantity) ||
    quantity < 1 ||
    quantity > 20
  ) {
    throw new Error(
      'Une quantité du panier est invalide.',
    );
  }

  return {
    source_type: sourceType,
    source_id: sourceId,
    quantity,
  };
}


function transactionalOrigin(): string {
  const env = import.meta.env as Record<
    string,
    string | undefined
  >;

  return (
    env.VITE_FORFEO_TRANSACTIONAL_URL ||
    env.VITE_FORFEO_COM_URL ||
    'https://forfeo.com'
  ).replace(/\/+$/, '');
}

export async function buildOfficialCheckoutHandoffUrl(
  cartItems: ExploreCartItem[],
): Promise<string> {
  if (
    !Array.isArray(cartItems) ||
    cartItems.length === 0
  ) {
    throw new Error(
      'Votre panier est vide.',
    );
  }

  if (cartItems.length > MAX_ITEMS) {
    throw new Error(
      `Le panier dépasse la limite de ${MAX_ITEMS} articles.`,
    );
  }

  const items = cartItems.map(
    normalizeItem,
  );

  const {
    data: sessionData,
    error: sessionError,
  } = await supabase.auth.getSession();

  const accessToken =
    sessionData.session?.access_token || '';

  if (
    sessionError ||
    !accessToken
  ) {
    throw new Error(
      'Vous devez être connecté pour poursuivre vers le paiement.',
    );
  }

  const {
    data,
    error,
  } = await supabase.functions.invoke(
    'issue-explore-checkout-handoff',
    {
      body: {
        items,
      },
      headers: {
        Authorization:
          `Bearer ${accessToken}`,
      },
    },
  );

  if (error) {
    throw new Error(
      'Le transfert sécurisé est temporairement indisponible. Veuillez réessayer.',
    );
  }

  const token =
    typeof data?.token === 'string'
      ? data.token.trim()
      : '';

  if (
    !/^[A-Za-z0-9_-]{43}$/.test(token)
  ) {
    throw new Error(
      'Le serveur n’a pas retourné un transfert sécurisé valide.',
    );
  }

  return (
    `${transactionalOrigin()}/checkout/explore` +
    `?handoff=${encodeURIComponent(token)}`
  );
}

// FORFEO_H3_B2F_HABITAT_HANDOFF_V1
export type OfficialHabitatSourceType =
  | 'habitat_credit_screening'
  | 'habitat_rent_payment';

type OfficialIntentResult = {
  intent_id?: unknown;
  intent_type?: unknown;
  expires_at?: unknown;
  ok?: unknown;
};

function officialIntentId(
  value: unknown,
): string {
  if (
    !value ||
    typeof value !== 'object' ||
    Array.isArray(value)
  ) {
    throw new Error(
      'Le serveur n’a pas retourné une intention de paiement valide.',
    );
  }

  const candidate =
    value as OfficialIntentResult;

  const intentId =
    text(candidate.intent_id, 100);

  if (!UUID.test(intentId)) {
    throw new Error(
      'Le serveur n’a pas retourné un identifiant de paiement valide.',
    );
  }

  return intentId;
}

export function currentHabitatPaymentPeriod():
  string {
  const now = new Date();

  const year =
    String(now.getFullYear());

  const month =
    String(now.getMonth() + 1)
      .padStart(2, '0');

  return `${year}-${month}`;
}

export async function buildOfficialHabitatCheckoutHandoffUrl(
  sourceType: OfficialHabitatSourceType,
  intentResult: unknown,
): Promise<string> {
  if (
    sourceType !==
      'habitat_credit_screening' &&
    sourceType !==
      'habitat_rent_payment'
  ) {
    throw new Error(
      'Le type de paiement Habitat est invalide.',
    );
  }

  const intentId =
    officialIntentId(intentResult);

  const {
    data: sessionData,
    error: sessionError,
  } = await supabase.auth.getSession();

  const accessToken =
    sessionData.session?.access_token || '';

  if (
    sessionError ||
    !accessToken
  ) {
    throw new Error(
      'Vous devez être connecté pour poursuivre vers le paiement.',
    );
  }

  const {
    data,
    error,
  } = await supabase.functions.invoke(
    'issue-explore-checkout-handoff',
    {
      body: {
        version: 3,
        items: [
          {
            source_type: sourceType,
            source_id: intentId,
            quantity: 1,
          },
        ],
      },
      headers: {
        Authorization:
          `Bearer ${accessToken}`,
      },
    },
  );

  if (error) {
    throw new Error(
      'Le transfert sécurisé est temporairement indisponible. Veuillez réessayer.',
    );
  }

  const token =
    typeof data?.token === 'string'
      ? data.token.trim()
      : '';

  if (
    !/^[A-Za-z0-9_-]{43}$/.test(token)
  ) {
    throw new Error(
      'Le serveur n’a pas retourné un transfert sécurisé valide.',
    );
  }

  return (
    `${transactionalOrigin()}/checkout/explore` +
    `?handoff=${encodeURIComponent(token)}`
  );
}
